import { useEffect } from "react";
import { match, navigate, usePath } from "./router";
import { useAuth } from "./auth";
import Dashboard from "./screens/host/Dashboard";
import HostGame from "./screens/host/HostGame";
import RemoteControl from "./screens/host/RemoteControl";
import Player from "./screens/player/Player";
import QuizBuilder from "./screens/admin/QuizBuilder";
import Quizzes from "./screens/admin/Quizzes";
import QuestionBank from "./screens/admin/QuestionBank";
import Results from "./screens/admin/Results";
import Account from "./screens/admin/Account";
import Team from "./screens/admin/Team";
import RecycleBin from "./screens/admin/RecycleBin";
import Logs from "./screens/admin/Logs";
import AllQuizzes from "./screens/admin/AllQuizzes";
import Live from "./screens/admin/Live";
import QuizView from "./screens/admin/QuizView";
import { Login, Signup } from "./screens/auth/Auth";
import StatesGallery, { StateError, StateLoading } from "./screens/states/States";

function Redirect({ to }: { to: string }) {
  useEffect(() => {
    navigate(to);
  }, [to]);
  return <StateLoading />;
}

export default function App() {
  const path = usePath();
  const { user, loading } = useAuth();

  // ── Public player surface (anonymous, no auth) ──
  if (path === "/play") return <Player />;
  const play = match("/play/:pin", path);
  if (play) return <Player pinParam={play.pin} />;
  // Host's phone controller — authenticates via the host token in the QR link,
  // so it's public (the phone is usually not signed in).
  const remote = match("/remote/:pin", path);
  if (remote) return <RemoteControl pin={remote.pin} />;
  if (path === "/states") return <StatesGallery />;

  if (loading) return <StateLoading />;

  // ── Auth pages ──
  if (path === "/login") return user ? <Redirect to="/" /> : <Login />;
  if (path === "/signup") return user ? <Redirect to="/" /> : <Signup />;

  // ── Everything below requires a signed-in host ──
  if (!user) return <Login />;

  if (path === "/" || path === "") return <Dashboard />;
  if (path === "/quizzes") return <Quizzes />;
  if (path === "/builder") return <QuizBuilder />;
  const builder = match("/builder/:id", path);
  if (builder) return <QuizBuilder quizId={builder.id} />;
  if (path === "/bank") return <QuestionBank />;
  if (path === "/results") return <Results />;
  const resultDetail = match("/results/:id", path);
  if (resultDetail) return <Results resultId={resultDetail.id} />;
  if (path === "/account") return <Account />;
  const quizView = match("/quiz/:id", path);
  if (quizView) return <QuizView quizId={quizView.id} />;
  const isAdminish = user.role === "admin" || user.role === "super_admin";
  const isSuper = user.role === "super_admin";
  if (path === "/team") return isAdminish ? <Team /> : <Redirect to="/account" />;
  if (path === "/recycle-bin") return isAdminish ? <RecycleBin /> : <Redirect to="/account" />;
  if (path === "/logs") return isSuper ? <Logs /> : <Redirect to="/account" />;
  if (path === "/all-quizzes") return isSuper ? <AllQuizzes /> : <Redirect to="/account" />;
  if (path === "/live") return isSuper ? <Live /> : <Redirect to="/account" />;

  const host = match("/host/:pin", path);
  if (host) return <HostGame pin={host.pin} />;

  return <StateError />;
}
